import { useEffect, useRef, useState } from "react";
import { fetchFlows, createFlow, deleteFlow } from "../../api/flows";
import styles from "./FlowSelector.module.css";

interface Flow {
  id: string;
  name: string;
  updated_at: number;
}

interface FlowSelectorProps {
  onSelect: (id: string) => void;
  onFieldManagement: () => void;
  onProductManagement: () => void;
}

function formatFlowName(id: string): string {
  return id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function nameToSlug(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function FlowSelector({ onSelect, onFieldManagement, onProductManagement }: FlowSelectorProps) {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New flow dialog
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [slugOverride, setSlugOverride] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation
  const [deletingFlow, setDeletingFlow] = useState<Flow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const slug = slugEdited ? slugOverride : nameToSlug(newName);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchFlows()
      .then(setFlows)
      .catch((err) => setError(err.message ?? 'Failed to load flows'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (creating) setTimeout(() => nameInputRef.current?.focus(), 50);
  }, [creating]);

  const openDialog = () => {
    setNewName('');
    setSlugOverride('');
    setSlugEdited(false);
    setCreateError(null);
    setCreating(true);
  };

  const closeDialog = () => setCreating(false);

  const handleNameChange = (v: string) => {
    setNewName(v);
    if (!slugEdited) setSlugOverride(nameToSlug(v));
  };

  const handleSlugChange = (v: string) => {
    setSlugOverride(v.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    setSlugEdited(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingFlow) return;
    setDeleteError(null);
    setDeleteLoading(true);
    try {
      await deleteFlow(deletingFlow.id);
      setDeletingFlow(null);
      load();
    } catch (err: any) {
      setDeleteError(err.message ?? 'Failed to delete flow');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !slug) return;
    setCreateError(null);
    setCreateLoading(true);
    try {
      const finalId = await createFlow(newName.trim(), slug);
      setCreating(false);
      load();
      onSelect(finalId);
    } catch (err: any) {
      setCreateError(err.message ?? 'Failed to create flow');
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className={styles.screen}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Configurator Flows</h1>
            <p className={styles.subtitle}>Select a flow to edit its steps and options.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={onFieldManagement}>Field Management</button>
            <button className="btn" onClick={onProductManagement}>Product Management</button>
            <button className="btn" onClick={() => { window.location.hash = '/debug-quotes'; }}>Debug Quotes</button>
            <button className="btn btn-accent" onClick={openDialog}>+ New flow</button>
          </div>
        </div>

        {loading && (
          <div className={styles.grid}>
            {[0, 1, 2].map(i => (
              <div key={i} className={styles.skeleton} />
            ))}
          </div>
        )}

        {error && (
          <div className={styles.errorBox}>
            <span>{error}</span>
            <button className="btn" onClick={load}>Retry</button>
          </div>
        )}

        {!loading && !error && flows.length === 0 && (
          <p className={styles.empty}>No flows yet. Create one to get started.</p>
        )}

        {!loading && !error && flows.length > 0 && (
          <div className={styles.grid}>
            {flows.map(flow => (
              <div key={flow.id} className={styles.card}>
                <div className={styles.cardBody}>
                  <h2 className={styles.flowName}>{flow.name || formatFlowName(flow.id)}</h2>
                  <p className={styles.flowId}>{flow.id}</p>
                  <p className={styles.updatedAt}>Updated {formatDate(flow.updated_at)}</p>
                </div>
                <div className={styles.cardActions}>
                  <button
                    className={`btn btn-accent ${styles.editBtn}`}
                    onClick={() => onSelect(flow.id)}
                  >
                    Edit flow →
                  </button>
                  <button
                    className="btn btn-danger-solid"
                    onClick={() => { setDeleteError(null); setDeletingFlow(flow); }}
                    title="Delete flow"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {deletingFlow && (
        <div className={styles.overlay} onClick={() => setDeletingFlow(null)}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h2 className={styles.dialogTitle}>Delete flow?</h2>
            <p className={styles.deleteWarning}>
              Are you sure you want to delete <strong>{deletingFlow.name || deletingFlow.id}</strong>?
              This action can be undone by an administrator if needed.
            </p>
            {deleteError && <p className={styles.createError}>{deleteError}</p>}
            <div className={styles.dialogActions}>
              <button className="btn" onClick={() => setDeletingFlow(null)} disabled={deleteLoading}>
                Cancel
              </button>
              <button className="btn btn-danger-solid" onClick={handleDeleteConfirm} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting…' : 'Delete flow'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New flow dialog */}
      {creating && (
        <div className={styles.overlay} onClick={closeDialog}>
          <div className={styles.dialog} onClick={e => e.stopPropagation()}>
            <h2 className={styles.dialogTitle}>New flow</h2>
            <form onSubmit={handleCreate}>
              <div className={styles.field}>
                <label className={styles.label}>Flow name</label>
                <input
                  ref={nameInputRef}
                  className={styles.input}
                  placeholder="e.g. Sauna Configurator"
                  value={newName}
                  onChange={e => handleNameChange(e.target.value)}
                  required
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>
                  Slug <span className={styles.labelMuted}>(auto-generated, editable)</span>
                </label>
                <input
                  className={styles.input}
                  placeholder="e.g. sauna-configurator"
                  value={slug}
                  onChange={e => handleSlugChange(e.target.value)}
                  required
                />
                {slug && (
                  <p className={styles.slugPreview}>ID: <code>{slug}</code></p>
                )}
              </div>
              {createError && (
                <p className={styles.createError}>{createError}</p>
              )}
              <div className={styles.dialogActions}>
                <button type="button" className="btn" onClick={closeDialog}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-accent"
                  disabled={createLoading || !newName.trim() || !slug}
                >
                  {createLoading ? 'Creating…' : 'Create flow'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
